// Match Play Bracket Generator
export class BracketGenerator {
  constructor(players, tournamentId) {
    this.players = players;
    this.tournamentId = tournamentId;
    this.brackets = [];
  }

  // Sort players by ranking (handicap or custom ranking)
  sortPlayersByRanking() {
    return [...this.players].sort((a, b) => {
      // If both have handicaps, sort by handicap (lower is better)
      if (a.handicap !== undefined && b.handicap !== undefined) {
        return a.handicap - b.handicap;
      }
      // If only one has handicap, prioritize it
      if (a.handicap !== undefined) return -1;
      if (b.handicap !== undefined) return 1;
      // If neither has handicap, maintain original order
      return 0;
    });
  }

  // Generate bracket with BYE handling
  generateBracket() {
    const sortedPlayers = this.sortPlayersByRanking();
    const numPlayers = sortedPlayers.length;
    
    // Calculate number of rounds needed
    const numRounds = Math.ceil(Math.log2(numPlayers));
    const bracketSize = Math.pow(2, numRounds);
    const numByes = bracketSize - numPlayers;

    console.log(`Generating bracket for ${numPlayers} players`);
    console.log(`Bracket size: ${bracketSize}, Byes needed: ${numByes}`);

    // Create initial bracket with BYEs
    const initialBracket = this.createInitialBracket(sortedPlayers, numByes);
    
    // Generate all rounds
    this.brackets = this.generateAllRounds(initialBracket, numRounds);
    
    return {
      brackets: this.brackets,
      numRounds,
      numByes,
      bracketSize
    };
  }

  createInitialBracket(players, numByes) {
    const bracket = [];
    const bracketSize = players.length + numByes;
    
    // Add players to bracket
    for (let i = 0; i < players.length; i++) {
      bracket.push({
        id: `match-1-${i}`,
        round: 1,
        position: i,
        player1: players[i],
        player2: null,
        winner: null,
        status: 'pending'
      });
    }

    // Add BYEs
    for (let i = 0; i < numByes; i++) {
      const byeIndex = players.length + i;
      bracket.push({
        id: `match-1-${byeIndex}`,
        round: 1,
        position: byeIndex,
        player1: { id: 'BYE', name: 'BYE', isBye: true },
        player2: null,
        winner: null,
        status: 'bye'
      });
    }

    return bracket;
  }

  generateAllRounds(initialBracket, numRounds) {
    const allRounds = [initialBracket];
    let currentRound = initialBracket;
    
    for (let round = 2; round <= numRounds; round++) {
      const nextRound = this.generateNextRound(currentRound, round);
      allRounds.push(nextRound);
      currentRound = nextRound;
    }

    return allRounds;
  }

  generateNextRound(previousRound, roundNumber) {
    const nextRound = [];
    const numMatches = Math.ceil(previousRound.length / 2);
    
    for (let i = 0; i < numMatches; i++) {
      nextRound.push({
        id: `match-${roundNumber}-${i}`,
        round: roundNumber,
        position: i,
        player1: null,
        player2: null,
        winner: null,
        status: 'pending'
      });
    }

    return nextRound;
  }

  // Update match result
  updateMatchResult(roundIndex, matchIndex, winner) {
    if (this.brackets[roundIndex] && this.brackets[roundIndex][matchIndex]) {
      this.brackets[roundIndex][matchIndex].winner = winner;
      this.brackets[roundIndex][matchIndex].status = 'completed';
      
      // Advance winner to next round
      this.advanceWinner(roundIndex, matchIndex, winner);
    }
  }

  advanceWinner(roundIndex, matchIndex, winner) {
    const nextRoundIndex = roundIndex + 1;
    if (nextRoundIndex < this.brackets.length) {
      const nextMatchIndex = Math.floor(matchIndex / 2);
      const nextMatch = this.brackets[nextRoundIndex][nextMatchIndex];
      
      if (nextMatch) {
        if (matchIndex % 2 === 0) {
          nextMatch.player1 = winner;
        } else {
          nextMatch.player2 = winner;
        }
        
        // If both players are set, mark as ready
        if (nextMatch.player1 && nextMatch.player2) {
          nextMatch.status = 'ready';
        }
      }
    }
  }

  // Get current active matches
  getActiveMatches() {
    const activeMatches = [];
    
    for (let roundIndex = 0; roundIndex < this.brackets.length; roundIndex++) {
      const round = this.brackets[roundIndex];
      for (let matchIndex = 0; matchIndex < round.length; matchIndex++) {
        const match = round[matchIndex];
        if (match.status === 'ready' && !match.winner) {
          activeMatches.push({
            ...match,
            roundIndex,
            matchIndex
          });
        }
      }
    }
    
    return activeMatches;
  }

  // Get tournament winner
  getTournamentWinner() {
    const finalRound = this.brackets[this.brackets.length - 1];
    if (finalRound && finalRound.length > 0) {
      const finalMatch = finalRound[0];
      return finalMatch.winner;
    }
    return null;
  }

  // Get bracket visualization data
  getBracketVisualization() {
    return this.brackets.map((round, roundIndex) => ({
      round: roundIndex + 1,
      matches: round.map((match, matchIndex) => ({
        id: match.id,
        player1: match.player1,
        player2: match.player2,
        winner: match.winner,
        status: match.status,
        position: match.position
      }))
    }));
  }
}

// Utility functions for bracket management
export const bracketUtils = {
  // Calculate number of rounds needed for a given number of players
  calculateRounds: (numPlayers) => Math.ceil(Math.log2(numPlayers)),
  
  // Calculate number of BYEs needed
  calculateByes: (numPlayers) => {
    const bracketSize = Math.pow(2, Math.ceil(Math.log2(numPlayers)));
    return bracketSize - numPlayers;
  },
  
  // Check if bracket is complete
  isBracketComplete: (brackets) => {
    const finalRound = brackets[brackets.length - 1];
    return finalRound && finalRound.length === 1 && finalRound[0].winner;
  },
  
  // Get next matches to be played
  getNextMatches: (brackets) => {
    for (let roundIndex = 0; roundIndex < brackets.length; roundIndex++) {
      const round = brackets[roundIndex];
      const readyMatches = round.filter(match => 
        match.status === 'ready' && !match.winner
      );
      if (readyMatches.length > 0) {
        return readyMatches;
      }
    }
    return [];
  }
};

